import { hexToRGBA } from '@/config/settings';

import type { ChartTheme } from '../theme';

import { axisScaleId, CHART_TRANSPARENT_COLOR } from './constants';
import { type MetricFillResolver, verticalFadeFill } from './gradientFill';
import { type LineGradientColors, lineGradientColors } from './lineGradient';
import type { MetricSeriesSpec } from './seriesTypes';

/**
 * The line-series styles a spec opts into beyond the defaults in `buildMetricChartData`: a
 * faded fill with its own opacities, a fill-only band, a threshold-coloured line and dashed
 * low-confidence stretches. Each returns dataset properties to spread over the defaults.
 */

/** How an uncertain stretch differs from the rest of the line. */
const UNCERTAIN_OPACITY = 0.7;
const UNCERTAIN_DASH: [number, number] = [6, 6];
/** A certain point on a line with uncertain ones: a small dot, so the two read apart. */
const CERTAIN_POINT_RADIUS = 1;
const CERTAIN_POINT_BORDER_WIDTH = 1;

/** The part of a Chart.js segment context the uncertain style reads. */
interface SegmentContext {
    p0DataIndex: number;
    p1DataIndex: number;
}

/** The part of a Chart.js point context the uncertain style reads. */
interface PointContext {
    dataIndex: number;
}

/** The line dataset properties these styles set; each sets only its own. */
export interface MetricLineStyle {
    backgroundColor?: MetricFillResolver;
    borderColor?: string | LineGradientColors['line'];
    pointBorderColor?: string | LineGradientColors['point'];
    pointBackgroundColor?: string | LineGradientColors['point'];
    pointHoverBorderColor?: LineGradientColors['point'];
    pointHoverBackgroundColor?: LineGradientColors['point'];
    pointRadius?: number | ((context: PointContext) => number);
    pointHoverRadius?: number;
    pointBorderWidth?: (context: PointContext) => number;
    segment?: {
        borderColor: (context: SegmentContext) => string | undefined;
        borderDash: (context: SegmentContext) => [number, number] | undefined;
    };
}

/**
 * `backgroundColor` for a filled series with `fillGradient`. `baseColor` is the series colour
 * before its `opacity`, which the fade replaces with its own; a fade can name its own colour.
 */
export function gradientFillStyle(
    series: MetricSeriesSpec,
    baseColor: string,
    theme: ChartTheme
): MetricLineStyle {
    if (!series.fill || !series.fillGradient) return {};
    if (series.fillGradient === true) return { backgroundColor: verticalFadeFill(baseColor) };
    const { color, topOpacity, bottomOpacity } = series.fillGradient;
    const from = color ? theme.color(color) : baseColor;
    return { backgroundColor: verticalFadeFill(from, topOpacity, bottomOpacity) };
}

/**
 * A fill-only band: no line and no markers at rest. The points keep the series colour, which
 * the tooltip swatch and a hover `marker` are drawn in.
 */
export function bandStyle(series: MetricSeriesSpec): MetricLineStyle {
    if (!series.hideLine) return {};
    return {
        borderColor: CHART_TRANSPARENT_COLOR,
        pointBorderColor: series.color,
        pointBackgroundColor: series.color,
        // A marker sets the radii itself, to show the hovered point.
        ...(series.marker ? {} : { pointRadius: 0, pointHoverRadius: 0 }),
    };
}

/** A line coloured by threshold bands on its axis; see `lineGradientColors`. */
export function gradientLineStyle(series: MetricSeriesSpec, theme: ChartTheme): MetricLineStyle {
    if (!series.lineGradient) return {};
    const colors = lineGradientColors(
        series.lineGradient,
        theme,
        axisScaleId(series.axis),
        series.color
    );
    return {
        borderColor: colors.line,
        pointBorderColor: colors.point,
        pointBackgroundColor: colors.point,
        pointHoverBorderColor: colors.point,
        pointHoverBackgroundColor: colors.point,
    };
}

/**
 * Dashed, paler stretches wherever either end is uncertain, and the uncertain points hidden
 * until hovered, so a low-confidence value never reads as a firm one.
 */
export function uncertainStyle(
    series: MetricSeriesSpec,
    baseColor: string,
    theme: ChartTheme
): MetricLineStyle {
    if (!series.uncertain) return {};
    const flags = series.uncertain.points;
    const isUncertain = (index: number) => flags[index] === true;
    const paler = hexToRGBA(
        series.uncertain.color ? theme.color(series.uncertain.color) : baseColor,
        UNCERTAIN_OPACITY
    );
    const touchesUncertain = (context: SegmentContext) =>
        isUncertain(context.p0DataIndex) || isUncertain(context.p1DataIndex);
    return {
        segment: {
            borderColor: (context: SegmentContext) =>
                touchesUncertain(context) ? paler : undefined,
            borderDash: (context: SegmentContext) =>
                touchesUncertain(context) ? UNCERTAIN_DASH : undefined,
        },
        pointRadius: (context: PointContext) =>
            isUncertain(context.dataIndex) ? 0 : CERTAIN_POINT_RADIUS,
        pointBorderWidth: (context: PointContext) =>
            isUncertain(context.dataIndex) ? 0 : CERTAIN_POINT_BORDER_WIDTH,
    };
}
